import { Check, Crown, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

interface SubscriptionPurchasedModalProps {
  productName: string;
  features: string[];
  onClose: () => void;
}

/**
 * Поздравительное модальное окно после успешной покупки подписки.
 * Показывается на ProfilePage, данные берутся из AppStore.pendingModal.
 */
export function SubscriptionPurchasedModal({
  productName,
  features,
  onClose,
}: SubscriptionPurchasedModalProps) {
  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-background/80 px-4 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative flex max-h-[90vh] w-full max-w-sm flex-col overflow-hidden rounded-[2rem] border border-border/50 bg-card shadow-2xl"
        >
          {/* Шапка с иконкой */}
          <div className="relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-violet-500/10 to-green-500/10 px-8 pb-6 pt-10">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", bounce: 0.55, delay: 0.15 }}
              className="relative flex h-24 w-24 items-center justify-center rounded-[2rem] bg-green-500/15"
            >
              {/* Вращающийся пунктирный контур — анимация успеха */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-[2rem] border-2 border-dashed border-green-500/30"
              />
              <Crown className="h-11 w-11 text-green-500" strokeWidth={1.5} />

              {/* Бейдж со звездой */}
              <motion.div
                initial={{ scale: 0, y: 8 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ delay: 0.5, type: "spring" }}
                className="absolute -bottom-3 -right-3 flex items-center gap-1 rounded-full border border-border bg-background px-2 py-1 shadow-lg"
              >
                <Sparkles className="h-3.5 w-3.5 text-violet-500" />
                <span className="text-xs font-bold text-violet-500">PRO</span>
              </motion.div>
            </motion.div>
          </div>

          {/* Контент */}
          <div className="flex flex-1 flex-col overflow-y-auto p-8 pt-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <h2 className="mb-1 text-center text-2xl font-bold tracking-tight">
                Поздравляем!
              </h2>
              <p className="mb-6 text-center text-[15px] text-muted-foreground">
                Вы приобрели подписку <span className="font-semibold text-foreground">{productName}</span>
              </p>
            </motion.div>

            {features.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="mb-6"
              >
                <p className="mb-3 text-sm font-medium text-muted-foreground">Вы получили доступ к:</p>
                <div className="flex flex-col gap-2">
                  {features.map((feature, idx) => (
                    <motion.div
                      key={feature}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + idx * 0.06 }}
                      className="flex items-start gap-3"
                    >
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500/15">
                        <Check className="h-3 w-3 text-green-500" strokeWidth={2.5} />
                      </div>
                      <span className="text-sm text-foreground">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileTap={{ scale: 0.97 }}
              onClick={onClose}
              className="mt-auto w-full rounded-2xl bg-gradient-to-r from-violet-600 to-violet-700 py-4 text-base font-semibold text-white shadow-sm"
            >
              Отлично!
            </motion.button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
